/**
 * Подсказка первого входа: подсветить одно место и объяснить его словами.
 *
 * Не тур на семь шагов и не модальное окно поверх всего. Человек пришёл
 * работать, а не читать инструкцию: подсвечиваем ровно тот элемент, о котором
 * говорим, оставляем его видимым и даём одну кнопку «Понятно».
 *
 * Якорь ищется по атрибуту `data-coach` в DOM. Так экраны остаются нетронутыми:
 * одна подпись на элемент вместо проброса ref через три уровня компонентов.
 */

import clsx from 'clsx'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import type { CoachKey } from '@/shared/api/hints'
import { haptic } from '@/shared/telegram'
import { Squiggle } from '@/shared/ui/decor'

/** Воздух вокруг подсвеченного элемента, чтобы он не касался края выреза. */
const PADDING = 8
/** Ширина карточки с текстом ограничена: длинная строка читается хуже. */
const CARD_WIDTH = 300
const MARGIN = 12
const GAP = 14
// Больше этой доли экрана подсветка не занимает.
//
// Раздел вроде ленты дня бывает выше экрана целиком: подсветив его весь, мы
// затемняем ровно ничего, а карточке с текстом не остаётся места ни сверху,
// ни снизу. Подсвечиваем начало раздела — этого достаточно, чтобы понять, о
// чём речь, и остаётся куда положить подпись.
const MAX_SPOT_SHARE = 0.55

interface Rect {
  top: number
  left: number
  width: number
  height: number
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function measure(element: Element): Rect {
  const box = element.getBoundingClientRect()
  const height = Math.min(box.height + PADDING * 2, window.innerHeight * MAX_SPOT_SHARE)
  return {
    top: box.top - PADDING,
    left: box.left - PADDING,
    width: box.width + PADDING * 2,
    height,
  }
}

export function CoachMark({
  anchor,
  title,
  text,
  step,
  total,
  onNext,
  onSkip,
}: {
  /** Значение `data-coach` у элемента, который подсвечиваем. */
  anchor: CoachKey
  title: string
  text: string
  step: number
  total: number
  onNext: () => void
  onSkip: () => void
}) {
  const [rect, setRect] = useState<Rect | null>(null)
  const frame = useRef(0)

  // Высоту карточки считаем по факту, а не прикидываем константой: текст
  // подсказок разной длины, и на трёх строках карточка на полсотни пикселей
  // выше, чем на двух. Прикидка ошибается ровно тогда, когда это заметно, —
  // на нижней части экрана, где карточка уезжает под край.
  // Ссылка через состояние, а не useRef: на первом рендере подсказки ещё нет
  // (позиция якоря не измерена), и эффект, навешенный на ref, увидел бы null
  // и больше не перезапустился — карточка осталась бы с нулевой высотой и,
  // как следствие, невидимой навсегда.
  const [card, setCard] = useState<HTMLDivElement | null>(null)
  const [cardHeight, setCardHeight] = useState(0)

  // Позицию пересчитываем каждый кадр, пока подсказка открыта. Кажется
  // расточительным, но альтернатива — ловить scroll, resize, дозагрузку данных
  // и завершение плавной прокрутки по отдельности, и всё равно промахиваться.
  // Одно getBoundingClientRect на кадр стоит доли миллисекунды.
  useLayoutEffect(() => {
    const element = document.querySelector(`[data-coach="${anchor}"]`)
    if (!element) return

    // Мгновенно, не плавно: замер идёт сразу после этой строки, а плавная
    // прокрутка к этому моменту ещё не началась — подсказка вставала бы по
    // позиции «до скролла» и на первом кадре промахивалась мимо элемента.
    // Не scrollIntoView: он выравнивает элемент, а нам нужна видимость
    // подсветки, которая у высокого раздела короче самого раздела. Сдвигаем
    // ровно настолько, чтобы она вошла в экран, и не трогаем ничего лишнего,
    // если она и так видна.
    const spot = measure(element)
    const above = MARGIN - spot.top
    const below = spot.top + spot.height - (window.innerHeight - MARGIN)
    if (above > 0) window.scrollBy(0, -above)
    else if (below > 0) window.scrollBy(0, below)

    const tick = () => {
      setRect(measure(element))
      frame.current = requestAnimationFrame(tick)
    }
    tick()
    return () => cancelAnimationFrame(frame.current)
  }, [anchor])

  // Замер синхронно на смену шага — и наблюдатель на всё остальное.
  //
  // Одного наблюдателя мало: его вызов приходит уже после отрисовки, и первый
  // кадр нового шага встал бы по высоте предыдущего — у подсказок текст разной
  // длины, и промах доходит до пары десятков пикселей. Одного замера тоже
  // мало: от поворота экрана текст перетекает, а шаг при этом не меняется.
  useLayoutEffect(() => {
    if (!card) return
    setCardHeight(card.offsetHeight)
    const observer = new ResizeObserver(() => setCardHeight(card.offsetHeight))
    observer.observe(card)
    return () => observer.disconnect()
  }, [card, title, text])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onSkip()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onSkip])

  if (!rect) return null

  // Карточка снизу, если внизу помещается; иначе сверху. Если не помещается
  // нигде (низкий экран, длинный текст) — с той стороны, где места больше, и
  // прижата к краю: перекрыть часть подсветки лучше, чем уехать за экран.
  const spaceBelow = window.innerHeight - MARGIN - (rect.top + rect.height + GAP)
  const spaceAbove = rect.top - GAP - MARGIN
  const below = spaceBelow >= cardHeight || spaceBelow >= spaceAbove
  const cardTop = clamp(
    below ? rect.top + rect.height + GAP : rect.top - GAP - cardHeight,
    MARGIN,
    Math.max(MARGIN, window.innerHeight - MARGIN - cardHeight),
  )
  const cardLeft = clamp(
    rect.left + rect.width / 2 - CARD_WIDTH / 2,
    MARGIN,
    Math.max(MARGIN, window.innerWidth - CARD_WIDTH - MARGIN),
  )

  const last = step === total

  return createPortal(
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label={title}>
      {/* Прозрачный слой ловит промахи мимо кнопки: тап по фону = «понятно».
          Затемнение рисует не он, а тень вокруг выреза — иначе подсвеченный
          элемент оказался бы под полупрозрачной плёнкой и потускнел. */}
      <button
        type="button"
        aria-label="Понятно"
        onClick={onNext}
        className="absolute inset-0 cursor-default"
      />

      <div
        aria-hidden
        className="pointer-events-none absolute rounded-[var(--radius-card)]"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: '0 0 0 9999px var(--color-scrim)',
          outline: '2px dashed var(--color-on-scrim)',
          outlineOffset: 2,
          animation: 'fade-in 260ms var(--ease-out-soft) both',
        }}
      />

      <div
        ref={setCard}
        className={clsx(
          'bg-surface absolute rounded-[var(--radius-card)] p-4',
          // Кольцо, как у шторки: на тёмной вуали поверхность карточки
          // сливается с фоном, и без границы она читается пятном.
          'shadow-[var(--shadow-sheet)] ring-1',
          'ring-[color-mix(in_srgb,var(--color-line)_60%,transparent)]',
        )}
        style={{
          // До первого замера сторона выбора ещё не известна. Показать
          // карточку и через кадр перебросить её — заметное дёрганье, поэтому
          // первый кадр она занимает место, но не видна.
          visibility: cardHeight ? undefined : 'hidden',
          top: cardTop,
          left: cardLeft,
          width: CARD_WIDTH,
          maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
          // `enter`, а не `sheet-up`: карточка появляется на своём месте.
          // Выезд снизу — движение нижней шторки, и подсказка, начинающая
          // путь за краем экрана, читалась бы как ещё одно окно.
          animation: 'enter 280ms var(--ease-out-soft) both',
        }}
      >
        <span className="inline-block">
          <h3 className="font-display text-lead leading-tight">{title}</h3>
          <Squiggle seed={title} className="mt-0.5" />
        </span>
        <p className="text-text-muted mt-2 text-note leading-snug">{text}</p>

        <div className="mt-4 flex items-center justify-between gap-3">
          <span className="text-text-muted text-caption tabular-nums" aria-hidden>
            {step} / {total}
          </span>
          <div className="flex items-center gap-1">
            {!last && (
              <button
                type="button"
                onClick={onSkip}
                className="press text-text-muted min-h-[44px] rounded-[var(--radius-button)] px-3 text-note"
              >
                Пропустить
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                haptic('tap')
                onNext()
              }}
              className={clsx(
                'press bg-accent text-on-accent min-h-[44px] rounded-[var(--radius-button)]',
                'px-4 text-note font-semibold',
              )}
            >
              {last ? 'Понятно' : 'Дальше'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
